/**
 * What a trip-reel media item is, now that it can be a video as well as a photo.
 *
 * `reelData[].photos` has always been `string[]` — either a full URL or a `media.stippl.io` key —
 * and it is built in three places (web editor, mobile, and `reelPreviewService` server-side) and
 * sliced server-side by `renderVideo` against `maxPhotosPerDestination`. So the string form has to
 * keep working exactly as it did; a video is either a string that looks like one, or an object for
 * the case where a caller needs to say where in the clip to start.
 *
 * Curated stock clips run 4–115 seconds and the usable few seconds are rarely at the head, which is
 * the whole reason `startAt` exists — without it a 40 second aerial contributes its first two
 * seconds of empty sky.
 */

export type ReelMediaItem = string | { src?: string; url?: string; startAt?: number; kind?: "video" | "photo" };

const VIDEO_SRC = /\.(mp4|mov|webm|m4v)(\?|$)/i;

/**
 * Duplicated deliberately from `socialreel/clipReelTiming.ts`, which carries the same predicate for
 * `clip-reel` (#1091). Both are one regex, and the two PRs are independent on purpose so either can
 * merge alone — fold them into one shared helper once both have landed.
 */
export const isVideoUrl = (src: string) => VIDEO_SRC.test(src);

// Trip photo objects carry `url`, not `src` -- the outro and the card fan read those straight off the
// trip -- so both spellings resolve here rather than each caller remembering the other one.
export const mediaSrc = (item: ReelMediaItem) => (typeof item === "string" ? item : (item?.src ?? item?.url ?? ""));

export const mediaStartAt = (item: ReelMediaItem) => (typeof item === "string" ? 0 : (item.startAt ?? 0));

export const isVideoMedia = (item: ReelMediaItem) => {
	if (typeof item !== "string" && item.kind) return item.kind === "video";
	return isVideoUrl(mediaSrc(item));
};

/** Full URL passes through; a bare key is a `media.stippl.io` upload, same rule photos always used. */
export const mediaUrl = (item: ReelMediaItem) => {
	const src = mediaSrc(item);
	if (!src) return "";
	if (src.startsWith("http://") || src.startsWith("https://")) return src;
	return `https://media.stippl.io/${src}`;
};
